import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  TrendingUp, TrendingDown, Download, Menu, XCircle, Check, SlidersHorizontal,
  AlertTriangle, Wallet, Clock, ArrowUpRight, Users, Package, CreditCard,
  Receipt, Target, ChevronRight, Globe, Store
} from 'lucide-react';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';

const HEAD = { fontFamily: "'Manrope', system-ui, sans-serif" };

const fmtIdr = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)}M`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}jt`;
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1e3)}rb`;
  return `${v}`;
};
const fmtNum = (n) => new Intl.NumberFormat('id-ID').format(n || 0);
const fmtShortDate = (s) => {
  if (!s) return '';
  const p = String(s).split('-');
  if (p.length === 3) return `${p[2]}/${p[1]}`;
  if (p.length === 2) return `${p[1]}/${p[0].slice(2)}`;
  return s;
};

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hari Ini' },
  { value: 'week', label: '7 Hari' },
  { value: 'month', label: '30 Hari' },
  { value: 'year', label: '1 Tahun' },
  { value: 'all', label: 'Semua' },
];
const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, color: 'text-green-500' },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram, color: 'text-pink-500' },
  { value: 'messenger', label: 'Messenger', icon: FaFacebookMessenger, color: 'text-blue-500' },
  { value: 'website', label: 'Website', icon: Globe, color: 'text-indigo-500' },
];

// Nama channel di database bisa beragam — tampilkan label yang manusiawi
const channelMeta = (raw) => {
  const c = String(raw || '').toLowerCase();
  if (c.includes('whatsapp') || c === 'wa') return { label: 'WhatsApp', icon: FaWhatsapp, color: 'text-green-500' };
  if (c.includes('instagram') || c === 'ig') return { label: 'Instagram', icon: FaInstagram, color: 'text-pink-500' };
  if (c.includes('messenger') || c.includes('facebook')) return { label: 'Messenger', icon: FaFacebookMessenger, color: 'text-blue-500' };
  if (c.includes('website') || c.includes('web') || c.includes('widget') || c.includes('livechat')) {
    return { label: 'Website (Live Chat)', icon: Globe, color: 'text-indigo-500' };
  }
  if (c.includes('manual') || c.includes('dashboard')) return { label: 'Input Manual', icon: Store, color: 'text-slate-500 dark:text-zinc-400' };
  if (!c || c === 'unknown') return { label: 'Tanpa Channel', icon: Store, color: 'text-slate-500 dark:text-zinc-400' };
  return { label: raw.charAt(0).toUpperCase() + raw.slice(1), icon: Store, color: 'text-slate-500 dark:text-zinc-400' };
};
const STATUS_COLORS = ['#10b981', '#f59e0b', '#6366f1', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899'];

const Card = ({ children, className = '', testid }) => (
  <div
    data-testid={testid}
    className={`bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-slate-300 dark:hover:border-zinc-700 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-black/40 ${className}`}
  >
    {children}
  </div>
);

const Label = ({ children, className = '' }) => (
  <p className={`text-[10px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.16em] text-slate-500 dark:text-zinc-500 ${className}`}>
    {children}
  </p>
);

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <h3 className="text-sm font-semibold text-slate-800 dark:text-zinc-100 flex items-center gap-2" style={HEAD}>
      {Icon && <Icon className="w-4 h-4 text-slate-500 dark:text-zinc-500" strokeWidth={1.75} />}
      {children}
    </h3>
    {right}
  </div>
);

const Empty = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-8 border border-dashed border-slate-200 dark:border-zinc-800 rounded-lg">
    <Icon className="w-6 h-6 text-slate-500 dark:text-zinc-500" strokeWidth={1.5} />
    <p className="text-xs text-slate-500 dark:text-zinc-500">{text}</p>
  </div>
);

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-lg shadow-xl px-3 py-2 text-xs">
      <p className="font-medium text-slate-500 dark:text-zinc-400 mb-1">{fmtShortDate(label)}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center justify-between gap-5 tabular-nums">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-semibold text-slate-900 dark:text-zinc-50">{fmtIdr(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const Bar = ({ pct, color }) => (
  <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
    <div className={`h-full rounded-full ${color} transition-[width] duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
  </div>
);

const Skeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-16 bg-slate-100 dark:bg-zinc-800 rounded-xl" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-100 dark:bg-zinc-800 rounded-xl" />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 h-72 bg-slate-100 dark:bg-zinc-800 rounded-xl" />
      <div className="h-72 bg-slate-100 dark:bg-zinc-800 rounded-xl" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map(i => <div key={i} className="h-56 bg-slate-100 dark:bg-zinc-800 rounded-xl" />)}
    </div>
  </div>
);

export default function FinancialReportPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [period, setPeriod] = useState('month');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const channelsParam = selectedChannels.length > 0 ? `&channels=${selectedChannels.join(',')}` : '';

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/financial/report?period=${period}${channelsParam}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  }, [period, selectedChannels, token]);

  useEffect(() => { if (token) fetchReport(); }, [fetchReport]);

  const toggleChannel = (ch) =>
    setSelectedChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/financial/export?period=${period}${channelsParam}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Laporan_Keuangan_${period}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('File Excel berhasil diunduh');
    } catch {
      toast.error('Gagal export Excel');
    } finally {
      setExporting(false);
    }
  };

  const kpis = data?.kpis || {};
  const exp = data?.expenses || {};
  const cust = data?.customers || {};
  const ins = data?.insights || {};
  const chartData = data?.chart_data || [];
  const statusData = data?.status_data || [];
  const channelData = data?.channel_data || [];
  const topProducts = data?.top_products || [];
  const paymentData = data?.payment_data || [];

  const up = (kpis.revenue_change || 0) >= 0;
  const TrendIcon = up ? TrendingUp : TrendingDown;
  const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || '30 Hari';
  const stuck = ins.stuck_verification || { count: 0, value: 0 };
  const custTotal = (cust.new_revenue || 0) + (cust.repeat_revenue || 0);
  const custSplit = [
    { name: 'Pelanggan Baru', value: cust.new_revenue || 0 },
    { name: 'Pelanggan Berulang', value: cust.repeat_revenue || 0 },
  ];
  const maxChannel = Math.max(1, ...channelData.map(c => c.revenue || 0));
  const maxPayment = Math.max(1, ...paymentData.map(p => p.total || 0));
  const totalStatus = statusData.reduce((s, x) => s + (x.count || 0), 0);

  const kpiCards = [
    {
      testid: 'kpi-net-revenue', label: 'Pendapatan Bersih', labelShort: 'Bersih', hero: true,
      value: exp.net_after_platform, note: 'Setelah biaya langganan Nerava', noteShort: 'Setelah biaya Nerava',
      tone: 'text-indigo-600 dark:text-indigo-400', chip: 'bg-indigo-50 dark:bg-indigo-500/10', icon: Wallet,
    },
    {
      testid: 'kpi-total-revenue', label: 'Uang Masuk',
      value: kpis.total_revenue, note: `${fmtNum(kpis.paid_orders)} pesanan lunas`,
      tone: 'text-emerald-600 dark:text-emerald-400', chip: 'bg-emerald-50 dark:bg-emerald-500/10', icon: ArrowUpRight,
    },
    {
      testid: 'kpi-pending-value', label: 'Piutang',
      value: kpis.pending_value, note: `${fmtNum(kpis.pending_orders)} pesanan menunggu`,
      tone: 'text-amber-600 dark:text-amber-400', chip: 'bg-amber-50 dark:bg-amber-500/10', icon: Clock,
    },
    {
      testid: 'kpi-total-expenses', label: 'Biaya Nerava',
      value: exp.platform_cost, note: `Paket ${fmtCompact(exp.subscription_cost)} · Kredit ${fmtCompact(exp.credit_cost)}`,
      tone: 'text-rose-600 dark:text-rose-400', chip: 'bg-rose-50 dark:bg-rose-500/10', icon: Receipt,
    },
  ];

  const secondary = [
    { label: 'Nilai / Pesanan', value: fmtIdr(kpis.avg_order_value) },
    { label: 'Masuk / Hari', value: fmtIdr(ins.daily_avg_revenue) },
    { label: 'Ongkir Tertagih', value: fmtIdr(kpis.total_shipping) },
    { label: 'Gross + Ongkir', value: fmtIdr(kpis.total_gross) },
    { label: 'Dibatalkan', value: fmtIdr(kpis.cancelled_value) },
    { label: 'Item / Pesanan', value: `${ins.avg_items_per_order || 0}` },
    { label: 'Rasio Lunas', value: `${ins.paid_rate_pct || 0}%` },
    { label: 'Rasio Batal', value: `${ins.cancel_rate_pct || 0}%` },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="lg:ml-64">
        {/* HEADER */}
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-[#0A0A0A]/80 border-b border-slate-200 dark:border-zinc-800/80">
          <div className="px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5 text-slate-600 dark:text-zinc-300" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={HEAD} data-testid="page-title">
                Laporan Keuangan
              </h1>
              <p className="hidden sm:block text-xs text-slate-500 dark:text-zinc-500">Ringkasan arus kas toko · {periodLabel}</p>
            </div>

            <div className="flex items-center gap-1.5 ml-auto">
              {/* channel chips (desktop) */}
              <div className="hidden sm:flex items-center gap-1.5">
                {CHANNEL_OPTIONS.map(ch => {
                  const active = selectedChannels.includes(ch.value);
                  return (
                    <button key={ch.value} onClick={() => toggleChannel(ch.value)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-slate-900 dark:bg-zinc-100 border-slate-900 dark:border-zinc-100 text-white dark:text-zinc-900'
                          : 'bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-300 hover:border-slate-400 dark:hover:border-zinc-600'
                      }`}
                      data-testid={`channel-filter-${ch.value}`}
                    >
                      {active && <Check className="w-3 h-3" />}
                      <ch.icon className={`w-3 h-3 ${active ? '' : ch.color}`} />
                      {ch.label}
                    </button>
                  );
                })}
                {selectedChannels.length > 0 && (
                  <button onClick={() => setSelectedChannels([])} className="text-xs text-slate-500 dark:text-zinc-400 hover:underline px-1">Reset</button>
                )}
              </div>
              {/* channel drawer (mobile) */}
              <button onClick={() => setFilterDrawerOpen(true)}
                className={`sm:hidden relative h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${
                  selectedChannels.length
                    ? 'border-slate-900 dark:border-zinc-100 text-slate-900 dark:text-zinc-100'
                    : 'border-slate-200 dark:border-zinc-800 text-slate-500 dark:text-zinc-400'
                }`}
                data-testid="filter-channel-drawer-mobile"
              >
                <SlidersHorizontal className="w-4 h-4" />
                {selectedChannels.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center">{selectedChannels.length}</span>
                )}
              </button>
              <button onClick={handleExport} disabled={exporting}
                className="h-9 px-2.5 sm:px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 text-xs font-medium text-slate-600 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors dark:bg-zinc-800"
                data-testid="export-excel-button" title="Export ke Excel">
                <Download className="w-4 h-4" />
                <span className="hidden md:inline">Excel</span>
              </button>
              <select value={period} onChange={e => setPeriod(e.target.value)}
                className="h-9 text-xs font-medium bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-lg px-2 text-slate-700 dark:text-zinc-200"
                data-testid="filter-period-dropdown">
                {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {selectedChannels.length > 0 && (
            <div className="px-3 pb-2 flex gap-1.5 sm:hidden" data-testid="active-channel-chips">
              {selectedChannels.map(ch => {
                const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
                return (
                  <span key={ch} className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 dark:border-zinc-800 text-[11px] text-slate-600 dark:text-zinc-300">
                    <opt.icon className={`w-2.5 h-2.5 ${opt.color}`} />{opt.label}
                    <button onClick={() => toggleChannel(ch)}><XCircle className="w-3 h-3" /></button>
                  </span>
                );
              })}
            </div>
          )}
        </header>

        <main className="px-3 sm:px-6 py-4 sm:py-6 max-w-7xl space-y-4 sm:space-y-6">
          {loading ? <Skeleton /> : (
            <>
              {/* 1. INSIGHT BANNER */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {stuck.count > 0 ? (
                  <div className="rounded-xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/[0.07] p-4 flex items-start gap-3" data-testid="insight-stuck-orders">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-200" style={HEAD}>
                        {stuck.count} pesanan menunggu verifikasi &gt; 24 jam
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5 tabular-nums">
                        Total {fmtIdr(stuck.value)} tertahan. Verifikasi sekarang agar uang tidak nyangkut.
                      </p>
                    </div>
                    <button onClick={() => navigate('/orders')}
                      className="shrink-0 flex items-center gap-1 text-xs font-semibold text-amber-900 dark:text-amber-200 hover:underline"
                      data-testid="kpi-review-stuck-orders">
                      Tinjau <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 flex items-start gap-3" data-testid="insight-stuck-orders">
                    <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-zinc-100" style={HEAD}>Tidak ada pesanan nyangkut</p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">Semua pesanan terverifikasi dalam 24 jam.</p>
                    </div>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 flex items-start gap-3" data-testid="insight-projection">
                  <Target className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-zinc-100 tabular-nums" style={HEAD}>
                      Proyeksi 30 hari: {fmtIdr(ins.projection_month_end)}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5 tabular-nums">
                      Berdasarkan rata-rata {fmtIdr(ins.daily_avg_revenue)}/hari
                      {ins.best_day?.date ? ` · hari terbaik ${fmtShortDate(ins.best_day.date)} (${fmtIdr(ins.best_day.revenue)})` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* 2. KPI BENTO */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
                {kpiCards.map(c => (
                  <Card key={c.testid} testid={c.testid} className="p-3 sm:p-5">
                    <div className="flex items-start justify-between gap-2">
                      <Label className="truncate">
                        <span className="sm:hidden">{c.labelShort || c.label}</span>
                        <span className="hidden sm:inline">{c.label}</span>
                      </Label>
                      <span className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center shrink-0 ${c.chip}`}>
                        <c.icon className={`w-3.5 h-3.5 ${c.tone}`} strokeWidth={2} />
                      </span>
                    </div>
                    <p className={`mt-1.5 sm:mt-3 font-semibold tracking-tight tabular-nums ${c.hero ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-2xl'} ${c.tone}`} style={HEAD}>
                      {fmtIdr(c.value)}
                    </p>
                    <div className="mt-1 sm:mt-1.5 flex items-center gap-2">
                      <p className="text-[11px] sm:text-xs text-slate-500 dark:text-zinc-400 truncate">
                        <span className="sm:hidden">{c.noteShort || c.note}</span>
                        <span className="hidden sm:inline">{c.note}</span>
                      </p>
                      {c.testid === 'kpi-total-revenue' && kpis.revenue_change !== undefined && (
                        <span className={`ml-auto shrink-0 inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          <TrendIcon className="w-3 h-3" />{Math.abs(kpis.revenue_change)}%
                        </span>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {/* 3. CHART + CUSTOMER SPLIT */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <Card testid="chart-revenue-trend" className="lg:col-span-3 p-4 sm:p-5">
                  <SectionTitle icon={TrendingUp}
                    right={
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-zinc-400">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Lunas</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />Belum bayar</span>
                      </div>
                    }>
                    Arus Pendapatan
                  </SectionTitle>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={chartData} margin={{ left: 0, right: 8, top: 4 }}>
                        <defs>
                          <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:opacity-20" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={fmtShortDate} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: '#94a3b8' }} width={44} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTip />} />
                        <Area type="monotone" dataKey="revenue" name="Lunas" stroke="#10b981" strokeWidth={2} fill="url(#gRev)" />
                        <Area type="monotone" dataKey="pending" name="Belum bayar" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" fill="none" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <Empty icon={TrendingUp} text="Belum ada transaksi pada periode ini" />}
                </Card>

                <Card testid="chart-customer-split" className="p-4 sm:p-5">
                  <SectionTitle icon={Users}>Pelanggan</SectionTitle>
                  {custTotal > 0 ? (
                    <>
                      <div className="relative">
                        <ResponsiveContainer width="100%" height={140}>
                          <PieChart>
                            <Pie data={custSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={62} paddingAngle={2} stroke="none">
                              <Cell fill="#6366f1" />
                              <Cell fill="#10b981" />
                            </Pie>
                            <Tooltip formatter={(v, n) => [fmtIdr(v), n]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <p className="text-[10px] text-slate-500 dark:text-zinc-500">Berulang</p>
                          <p className="text-base font-semibold text-slate-900 dark:text-zinc-50 tabular-nums" style={HEAD}>
                            {Math.round(((cust.repeat_revenue || 0) / custTotal) * 100)}%
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2 text-slate-600 dark:text-zinc-300"><span className="w-2 h-2 rounded-full bg-indigo-500" />Baru ({cust.new_count})</span>
                          <span className="font-semibold tabular-nums text-slate-900 dark:text-zinc-100">{fmtIdr(cust.new_revenue)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2 text-slate-600 dark:text-zinc-300"><span className="w-2 h-2 rounded-full bg-emerald-500" />Berulang ({cust.repeat_count})</span>
                          <span className="font-semibold tabular-nums text-slate-900 dark:text-zinc-100">{fmtIdr(cust.repeat_revenue)}</span>
                        </div>
                      </div>
                    </>
                  ) : <Empty icon={Users} text="Belum ada pelanggan lunas" />}
                </Card>
              </div>

              {/* 4. SECONDARY METRICS STRIP */}
              <Card testid="secondary-metrics" className="p-1.5">
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8">
                  {secondary.map((m, i) => (
                    <div key={i} className={`px-3 py-2.5 border-slate-100 dark:border-zinc-800 ${i % 2 === 1 ? 'border-l' : ''} ${i >= 2 ? 'border-t md:border-t-0' : ''} ${i % 4 !== 0 ? 'md:border-l' : ''} ${i >= 4 ? 'md:border-t xl:border-t-0' : ''} ${i > 0 ? 'xl:border-l' : ''}`}>
                      <Label className="truncate">{m.label}</Label>
                      <p className="mt-1 text-xs sm:text-sm font-semibold text-slate-900 dark:text-zinc-100 tabular-nums truncate" style={HEAD}>{m.value}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* 5. CHANNEL + PAYMENT + STATUS */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card testid="chart-channel-performance" className="p-4 sm:p-5">
                  <SectionTitle icon={ArrowUpRight}>Pendapatan per Channel</SectionTitle>
                  {channelData.length > 0 ? (
                    <div className="space-y-3.5">
                      {[...channelData].sort((a, b) => b.revenue - a.revenue).map((c, i) => {
                        const meta = channelMeta(c.channel);
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                              <span className="flex items-center gap-1.5 min-w-0 text-slate-700 dark:text-zinc-200 font-medium">
                                <meta.icon className={`w-3.5 h-3.5 shrink-0 ${meta.color}`} />
                                <span className="truncate">{meta.label}</span>
                              </span>
                              <span className="tabular-nums text-slate-500 dark:text-zinc-400 whitespace-nowrap shrink-0">
                                {fmtIdr(c.revenue)} · {c.count}x
                              </span>
                            </div>
                            <Bar pct={(c.revenue / maxChannel) * 100} color="bg-indigo-500" />
                          </div>
                        );
                      })}
                    </div>
                  ) : <Empty icon={ArrowUpRight} text="Belum ada data channel" />}
                </Card>

                <Card testid="payment-methods" className="p-4 sm:p-5">
                  <SectionTitle icon={CreditCard}>Metode Pembayaran</SectionTitle>
                  {paymentData.length > 0 ? (
                    <div className="space-y-3.5">
                      {[...paymentData].sort((a, b) => b.total - a.total).map((p, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                            <span className="text-slate-700 dark:text-zinc-200 font-medium truncate">{p.method}</span>
                            <span className="tabular-nums text-slate-500 dark:text-zinc-400 whitespace-nowrap shrink-0">{fmtIdr(p.total)} · {p.count}x</span>
                          </div>
                          <Bar pct={(p.total / maxPayment) * 100} color="bg-emerald-500" />
                        </div>
                      ))}
                    </div>
                  ) : <Empty icon={CreditCard} text="Belum ada pembayaran masuk" />}
                </Card>

                <Card testid="status-breakdown" className="p-4 sm:p-5">
                  <SectionTitle icon={Clock}>Status Pesanan</SectionTitle>
                  {statusData.length > 0 ? (
                    <div className="space-y-2.5">
                      {[...statusData].sort((a, b) => b.count - a.count).map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }} />
                          <span className="text-xs text-slate-700 dark:text-zinc-200 truncate flex-1" title={s.status}>{s.status}</span>
                          <span className="text-xs tabular-nums text-slate-500 dark:text-zinc-400 whitespace-nowrap">{fmtIdr(s.value)}</span>
                          <span className="text-xs font-semibold tabular-nums text-slate-900 dark:text-zinc-100 whitespace-nowrap">
                            {s.count}<span className="text-slate-500 dark:text-zinc-500 font-normal"> ({totalStatus ? Math.round((s.count / totalStatus) * 100) : 0}%)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : <Empty icon={Clock} text="Belum ada pesanan" />}
                </Card>
              </div>

              {/* 6. TOP PRODUCTS + TOP CUSTOMERS + EXPENSES */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card testid="table-top-products" className="lg:col-span-2 p-4 sm:p-5">
                  <SectionTitle icon={Package}>Produk Terlaris</SectionTitle>
                  {topProducts.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left border-b border-slate-100 dark:border-zinc-800">
                          <th className="pb-2 font-medium text-slate-500 dark:text-zinc-500 w-8">#</th>
                          <th className="pb-2 font-medium text-slate-500 dark:text-zinc-500">Produk</th>
                          <th className="pb-2 font-medium text-slate-500 dark:text-zinc-500 text-right">Terjual</th>
                          <th className="pb-2 font-medium text-slate-500 dark:text-zinc-500 text-right">Pendapatan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProducts.map((p, i) => (
                          <tr key={i} className="border-b border-slate-50 dark:border-zinc-800/60 last:border-0">
                            <td className="py-2 text-slate-500 dark:text-zinc-500 tabular-nums">{i + 1}</td>
                            <td className="py-2 text-slate-800 dark:text-zinc-100 font-medium truncate max-w-[220px]">{p.product}</td>
                            <td className="py-2 text-right tabular-nums text-slate-500 dark:text-zinc-400">{fmtNum(p.quantity)}</td>
                            <td className="py-2 text-right tabular-nums font-semibold text-slate-900 dark:text-zinc-50">{fmtIdr(p.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <Empty icon={Package} text="Belum ada produk terjual" />}
                </Card>

                <div className="space-y-4">
                  <Card testid="top-customers" className="p-4 sm:p-5">
                    <SectionTitle icon={Users}>Pelanggan Teratas</SectionTitle>
                    {(cust.top_customers || []).length > 0 ? (
                      <div className="space-y-2.5">
                        {cust.top_customers.map((c, i) => (
                          <div key={i} className="flex items-center gap-2.5">
                            <span className="w-6 h-6 rounded-full bg-slate-100 dark:bg-zinc-800 text-[10px] font-semibold text-slate-500 dark:text-zinc-400 flex items-center justify-center shrink-0">{i + 1}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-slate-800 dark:text-zinc-100 truncate">{c.customer}</p>
                              <p className="text-[10px] text-slate-500 dark:text-zinc-500">{c.orders}x pesanan</p>
                            </div>
                            <span className="text-xs font-semibold tabular-nums text-slate-900 dark:text-zinc-50">{fmtIdr(c.revenue)}</span>
                          </div>
                        ))}
                      </div>
                    ) : <Empty icon={Users} text="Belum ada pelanggan" />}
                  </Card>

                  <Card testid="expense-detail" className="p-4 sm:p-5">
                    <SectionTitle icon={Receipt}>Biaya Nerava</SectionTitle>
                    {(exp.items || []).length > 0 ? (
                      <div className="space-y-2">
                        {exp.items.map((it, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-slate-700 dark:text-zinc-200 truncate">{it.label}</span>
                            <span className="tabular-nums font-semibold text-rose-600 dark:text-rose-400">−{fmtIdr(it.amount)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100 dark:border-zinc-800 text-xs">
                          <span className="font-medium text-slate-500 dark:text-zinc-400">Total</span>
                          <span className="tabular-nums font-bold text-rose-600 dark:text-rose-400">−{fmtIdr(exp.platform_cost)}</span>
                        </div>
                      </div>
                    ) : <Empty icon={Receipt} text="Belum ada biaya pada periode ini" />}
                  </Card>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* MOBILE CHANNEL DRAWER */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="bg-white dark:bg-zinc-900">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2" style={HEAD}>
              <SlidersHorizontal className="w-4 h-4" />Filter Channel
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-1" data-testid="filter-drawer-content">
            {CHANNEL_OPTIONS.map(ch => {
              const active = selectedChannels.includes(ch.value);
              return (
                <button key={ch.value} onClick={() => toggleChannel(ch.value)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    active ? 'bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-zinc-50 font-medium' : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800/60'
                  }`}
                  data-testid={`drawer-channel-${ch.value}`}>
                  <span className="flex items-center gap-2"><ch.icon className={`w-4 h-4 ${ch.color}`} />{ch.label}</span>
                  {active && <Check className="w-4 h-4" />}
                </button>
              );
            })}
            {selectedChannels.length > 0 && (
              <button onClick={() => { setSelectedChannels([]); setFilterDrawerOpen(false); }}
                className="w-full text-center text-sm text-slate-500 dark:text-zinc-400 hover:underline mt-2 py-2">
                Reset Filter
              </button>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
